import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Quote, Truck, RefreshCw, Shield, Clock, ChevronRight, Instagram, Watch, Briefcase, Crown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 11: ALLURE
 * Style: Sophisticated Men's Fashion, Premium, Classic
 * Best for: Men's formal wear, Sherwanis, Suits, Premium kurtas
 * Light theme with navy/slate accents
 */
const Template11 = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('formal');
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // Categories - Men's premium fashion
    const categories = [
        { name: 'Sherwanis', count: '120+', image: 'https://placehold.co/500x650/F8FAFC/1E3A5F?text=Sherwanis', tag: 'Wedding' },
        { name: 'Suits & Blazers', count: '180+', image: 'https://placehold.co/500x650/F1F5F9/1E3A5F?text=Suits', tag: 'Formal' },
        { name: 'Designer Kurtas', count: '200+', image: 'https://placehold.co/500x650/E2E8F0/1E3A5F?text=Kurtas', tag: 'Festive' },
        { name: 'Bandhgalas', count: '80+', image: 'https://placehold.co/500x650/CBD5E1/1E3A5F?text=Bandhgalas', tag: 'Elite' }
    ];

    // Products - Premium Indian men's wear
    const products = [
        { id: 1, name: 'Royal Blue Sherwani Set', price: '\u20B928,999', originalPrice: '\u20B938,999', image: 'https://placehold.co/400x500/F8FAFC/1E3A5F?text=Sherwani+1', badge: 'Bestseller', category: 'formal' },
        { id: 2, name: 'Italian Wool Blazer', price: '\u20B918,499', originalPrice: '\u20B924,999', image: 'https://placehold.co/400x500/F1F5F9/1E3A5F?text=Blazer+1', badge: 'Premium', category: 'formal' },
        { id: 3, name: 'Silk Kurta Pajama Set', price: '\u20B98,999', originalPrice: '\u20B912,999', image: 'https://placehold.co/400x500/E2E8F0/1E3A5F?text=Kurta+1', badge: 'Festive', category: 'ethnic' },
        { id: 4, name: 'Nehru Jacket Ensemble', price: '\u20B912,499', originalPrice: '\u20B916,999', image: 'https://placehold.co/400x500/CBD5E1/1E3A5F?text=Nehru+1', badge: 'New', category: 'ethnic' },
        { id: 5, name: 'Tailored Formal Suit', price: '\u20B932,999', originalPrice: '\u20B945,999', image: 'https://placehold.co/400x500/F8FAFC/1E3A5F?text=Suit+1', badge: 'Luxury', category: 'formal' },
        { id: 6, name: 'Embroidered Jodhpuri', price: '\u20B922,499', originalPrice: '\u20B929,999', image: 'https://placehold.co/400x500/F1F5F9/1E3A5F?text=Jodhpuri+1', badge: 'Wedding', category: 'ethnic' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'The sherwani I wore for my wedding was absolutely perfect. The fit, the fabric, the embroidery - everything was impeccable.', name: 'Rahul Malhotra', location: 'Delhi', event: 'Groom' },
        { text: 'Finally found a brand that understands what Indian men need. Their suits rival the best international brands.', name: 'Arjun Kapoor', location: 'Mumbai', event: 'Corporate Executive' },
        { text: 'The Nehru jacket collection is outstanding. Perfect for formal events and incredibly comfortable.', name: 'Vikram Singh', location: 'Jaipur', event: 'Wedding Guest' }
    ];

    // Style guide sections
    const styleGuide = [
        { title: 'Wedding Season', description: 'Curated looks for every ceremony', image: 'https://placehold.co/600x400/F8FAFC/1E3A5F?text=Wedding+Style' },
        { title: 'Corporate Excellence', description: 'Power dressing for professionals', image: 'https://placehold.co/600x400/F1F5F9/1E3A5F?text=Corporate+Style' },
        { title: 'Festive Collection', description: 'Celebrate in refined elegance', image: 'https://placehold.co/600x400/E2E8F0/1E3A5F?text=Festive+Style' }
    ];

    // Mobile testimonial auto-rotate
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* ===== HERO SECTION ===== */}
            {/* Mobile Hero: stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
                    <div className="relative aspect-[3/4] max-w-[80%] mx-auto mt-8">
                        <div className="absolute inset-0 bg-blue-900/10 rounded-lg transform rotate-3" />
                        <div className="relative z-10 overflow-hidden rounded-lg shadow-2xl">
                            <img
                                src="https://placehold.co/600x800/F8FAFC/1E3A5F?text=Gentleman"
                                alt="Premium Menswear"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
                <div className="bg-slate-900 px-5 py-8 text-white">
                    <div className="flex items-center gap-2 mb-3">
                        <Crown className="w-4 h-4 text-blue-300" />
                        <span className="text-blue-300 text-xs tracking-wider uppercase">Premium Menswear</span>
                    </div>
                    <h1 className="text-3xl font-light leading-[1.1] mb-3">
                        Dress for
                        <span className="block text-blue-300 font-medium">Success</span>
                    </h1>
                    <p className="text-slate-400 text-[14px] leading-relaxed mb-6">
                        Discover India's finest collection of premium menswear. From boardroom to baraat, dress with distinction.
                    </p>
                    <button
                        onClick={() => navigate('/template11/shop')}
                        className="w-full py-4 bg-white text-slate-900 text-sm font-medium tracking-wider uppercase flex items-center justify-center gap-3"
                    >
                        Shop Collection <ArrowRight className="w-4 h-4" />
                    </button>
                    <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
                        <div>
                            <p className="text-2xl font-light text-white">25+</p>
                            <p className="text-xs text-slate-500">Years</p>
                        </div>
                        <div>
                            <p className="text-2xl font-light text-white">50k+</p>
                            <p className="text-xs text-slate-500">Happy Grooms</p>
                        </div>
                        <div>
                            <p className="text-2xl font-light text-white">15</p>
                            <p className="text-xs text-slate-500">Stores</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop Hero: Sophisticated - EXACT ORIGINAL */}
            <section className="hidden lg:block relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
                {/* Pattern Overlay */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, #1E3A5F 1px, transparent 0)',
                        backgroundSize: '40px 40px'
                    }} />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-16">
                        {/* Text Content */}
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900/5 rounded-full mb-6">
                                <Crown className="w-4 h-4 text-slate-700" />
                                <span className="text-slate-700 text-sm font-medium tracking-wider uppercase">Premium Menswear</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-slate-900 leading-[1.1] mb-6">
                                Dress for
                                <span className="block text-blue-900 font-medium">Success</span>
                            </h1>
                            <p className="text-slate-600 text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                                Discover India's finest collection of premium menswear.
                                From boardroom to baraat, dress with distinction.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template11/shop')}
                                    className="group px-8 py-4 bg-slate-900 text-white text-sm font-medium tracking-wider uppercase hover:bg-slate-800 transition-all duration-300 flex items-center justify-center gap-3"
                                >
                                    Shop Collection
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-8 py-4 border-2 border-slate-300 text-slate-700 text-sm font-medium tracking-wider uppercase hover:border-slate-900 transition-all duration-300">
                                    Book Appointment
                                </button>
                            </div>

                            {/* Trust Indicators */}
                            <div className="flex justify-center lg:justify-start gap-8 mt-12 pt-8 border-t border-slate-200">
                                <div>
                                    <p className="text-3xl font-light text-slate-900">25+</p>
                                    <p className="text-sm text-slate-500">Years of Excellence</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-light text-slate-900">50k+</p>
                                    <p className="text-sm text-slate-500">Happy Grooms</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-light text-slate-900">15</p>
                                    <p className="text-sm text-slate-500">Stores Nationwide</p>
                                </div>
                            </div>
                        </div>

                        {/* Hero Image */}
                        <div className="relative">
                            <div className="relative aspect-[3/4] max-w-lg mx-auto">
                                <div className="absolute inset-0 bg-blue-900/10 rounded-lg transform rotate-3" />
                                <div className="relative z-10 overflow-hidden rounded-lg shadow-2xl">
                                    <img
                                        src="https://placehold.co/600x800/F8FAFC/1E3A5F?text=Gentleman"
                                        alt="Premium Menswear"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                {/* Floating Badge */}
                                <div className="absolute -left-4 top-1/4 bg-white shadow-xl px-5 py-4 rounded-lg z-20">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                            <Watch className="w-5 h-5 text-slate-700" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Bespoke Tailoring</p>
                                            <p className="text-slate-900 font-semibold">Available</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== BRAND STATEMENT ===== */}
            <section className="py-14 lg:py-24 bg-slate-900 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-blue-300 text-sm tracking-[0.3em] uppercase mb-6">Our Philosophy</p>
                    <h2 className="text-2xl lg:text-4xl font-light text-white mb-6 leading-relaxed">
                        "A well-dressed man is a statement of self-respect and respect for others."
                    </h2>
                    <p className="text-slate-400 text-base lg:text-lg">
                        At Allure, we craft garments that command respect and exude confidence.
                    </p>
                </div>
            </section>

            {/* ===== CATEGORIES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <span className="text-blue-800 text-xs font-medium tracking-[0.2em] uppercase">Shop By Category</span>
                    <h2 className="text-xl font-light text-slate-900 mt-2 mb-5">Curated Collections</h2>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                            {categories.map((category, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate('/template11/shop')}
                                    className="flex-shrink-0 w-[68vw] snap-start cursor-pointer group"
                                >
                                    <div className="relative overflow-hidden rounded-lg">
                                        <div className="aspect-[4/5] overflow-hidden">
                                            <img
                                                src={category.image}
                                                alt={category.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-800/20 to-transparent" />
                                        <div className="absolute top-3 left-3">
                                            <span className="px-2 py-0.5 bg-blue-900 text-white text-[10px] font-medium rounded">
                                                {category.tag}
                                            </span>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-4">
                                            <h3 className="text-white text-lg font-medium">{category.name}</h3>
                                            <p className="text-slate-300 text-xs mt-1">{category.count} Styles</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* Desktop: Categories - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-blue-800 text-sm font-medium tracking-[0.2em] uppercase">Shop By Category</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mt-3">Curated Collections</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template11/shop')}
                                className="group cursor-pointer relative overflow-hidden rounded-lg"
                            >
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-800/20 to-transparent" />
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 bg-blue-900 text-white text-xs font-medium rounded">
                                        {category.tag}
                                    </span>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                    <h3 className="text-white text-lg lg:text-xl font-medium">{category.name}</h3>
                                    <p className="text-slate-300 text-sm mt-1">{category.count} Styles</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== FEATURED PRODUCTS ===== */}
            {/* Mobile: horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 bg-slate-50">
                <div className="px-5">
                    <div className="flex items-end justify-between mb-3">
                        <div>
                            <span className="text-blue-800 text-xs font-medium tracking-[0.2em] uppercase">Featured</span>
                            <h2 className="text-xl font-light text-slate-900 mt-2">Signature Pieces</h2>
                        </div>
                    </div>
                    {/* Tabs */}
                    <div className="flex gap-3 mb-5">
                        <button
                            onClick={() => setActiveTab('formal')}
                            className={`px-4 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ${
                                activeTab === 'formal'
                                    ? 'bg-slate-900 text-white'
                                    : 'bg-white text-slate-600'
                            }`}
                        >
                            Formal
                        </button>
                        <button
                            onClick={() => setActiveTab('ethnic')}
                            className={`px-4 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ${
                                activeTab === 'ethnic'
                                    ? 'bg-slate-900 text-white'
                                    : 'bg-white text-slate-600'
                            }`}
                        >
                            Ethnic
                        </button>
                    </div>
                    <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                        {products
                            .filter(p => activeTab === 'all' || p.category === activeTab)
                            .map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template11/shop')}
                                className="flex-shrink-0 w-[44vw] snap-start cursor-pointer group"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-white mb-2 shadow-sm">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-blue-900 text-white">
                                        {product.badge}
                                    </span>
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center"
                                    >
                                        <Heart className={`w-4 h-4 ${wishlist[product.id] ? 'fill-blue-900 text-blue-900' : 'text-slate-500'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-md">
                                        <Plus className="w-4 h-4 text-slate-700" />
                                    </button>
                                </div>
                                <h3 className="text-slate-900 text-xs font-medium line-clamp-1">{product.name}</h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-slate-900 text-sm font-bold">{product.price}</span>
                                    <span className="text-slate-400 text-xs line-through">{product.originalPrice}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template11/shop')}
                        className="w-full mt-5 py-3.5 border-2 border-slate-900 text-slate-900 text-sm font-medium tracking-wider uppercase"
                    >
                        View All Products
                    </button>
                </div>
            </section>

            {/* Desktop: Products with tabs - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-blue-800 text-sm font-medium tracking-[0.2em] uppercase">Featured</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mt-3">Signature Pieces</h2>
                        </div>
                        {/* Tabs */}
                        <div className="flex gap-4 mt-6 lg:mt-0">
                            <button
                                onClick={() => setActiveTab('formal')}
                                className={`px-6 py-2 text-sm font-medium tracking-wider uppercase transition-colors ${
                                    activeTab === 'formal'
                                        ? 'bg-slate-900 text-white'
                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                Formal
                            </button>
                            <button
                                onClick={() => setActiveTab('ethnic')}
                                className={`px-6 py-2 text-sm font-medium tracking-wider uppercase transition-colors ${
                                    activeTab === 'ethnic'
                                        ? 'bg-slate-900 text-white'
                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                Ethnic
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {products
                            .filter(p => activeTab === 'all' || p.category === activeTab)
                            .slice(0, 6)
                            .map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template11/shop')}
                                className="group cursor-pointer bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <span className="absolute top-3 left-3 px-3 py-1 bg-blue-900 text-white text-xs font-medium">
                                        {product.badge}
                                    </span>
                                    <button className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white">
                                        <Heart className="w-5 h-5 text-slate-700" />
                                    </button>
                                </div>
                                <div className="p-4 lg:p-6">
                                    <h3 className="text-slate-900 font-medium group-hover:text-blue-900 transition-colors line-clamp-1 mb-2">
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-slate-900 font-bold text-lg">{product.price}</span>
                                        <span className="text-slate-400 text-sm line-through">{product.originalPrice}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template11/shop')}
                            className="group inline-flex items-center gap-3 px-8 py-4 border-2 border-slate-900 text-slate-900 text-sm font-medium tracking-wider uppercase hover:bg-slate-900 hover:text-white transition-all duration-300"
                        >
                            View All Products
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== STYLE GUIDE ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <span className="text-blue-800 text-xs font-medium tracking-[0.2em] uppercase">Style Guide</span>
                    <h2 className="text-xl font-light text-slate-900 mt-2 mb-5">Dress for Every Occasion</h2>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                            {styleGuide.map((guide, index) => (
                                <div key={index} className="flex-shrink-0 w-[75vw] snap-start cursor-pointer group">
                                    <div className="aspect-[3/2] overflow-hidden rounded-lg mb-3">
                                        <img
                                            src={guide.image}
                                            alt={guide.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <h3 className="text-slate-900 text-base font-medium mb-1">{guide.title}</h3>
                                    <p className="text-slate-500 text-xs">{guide.description}</p>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* Desktop: Style Guide - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-blue-800 text-sm font-medium tracking-[0.2em] uppercase">Style Guide</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mt-3">Dress for Every Occasion</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {styleGuide.map((guide, index) => (
                            <div key={index} className="group cursor-pointer">
                                <div className="aspect-[3/2] overflow-hidden rounded-lg mb-4">
                                    <img
                                        src={guide.image}
                                        alt={guide.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <h3 className="text-slate-900 text-xl font-medium mb-2 group-hover:text-blue-900 transition-colors">
                                    {guide.title}
                                </h3>
                                <p className="text-slate-500">{guide.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== BESPOKE SECTION ===== */}
            {/* Mobile: full-bleed stacked */}
            <section className="lg:hidden py-10 bg-gradient-to-br from-slate-100 to-blue-50">
                <div>
                    <div className="grid grid-cols-2 gap-2 px-5 mb-6">
                        <div className="aspect-[4/5] overflow-hidden rounded-lg">
                            <img src="https://placehold.co/400x500/F8FAFC/1E3A5F?text=Tailoring+1" alt="Tailoring" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-[4/5] overflow-hidden rounded-lg mt-4">
                            <img src="https://placehold.co/400x500/CBD5E1/1E3A5F?text=Tailoring+2" alt="Tailoring" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
                <div className="px-5">
                    <span className="text-blue-800 text-xs font-medium tracking-[0.2em] uppercase">Bespoke Service</span>
                    <h2 className="text-xl font-light text-slate-900 mt-2 mb-4">Made to Measure Excellence</h2>
                    <p className="text-slate-600 text-[14px] leading-relaxed mb-5">
                        Experience the art of bespoke tailoring. Our master tailors craft each garment to your exact specifications, ensuring a perfect fit that reflects your unique style.
                    </p>
                    <div className="space-y-3 mb-6">
                        {[
                            'Personal consultation with style experts',
                            'Premium fabric selection from finest mills',
                            'Multiple fittings for perfect fit',
                            'Delivery in 3-4 weeks'
                        ].map((item, index) => (
                            <div key={index} className="flex items-center gap-3">
                                <div className="w-5 h-5 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                                    <ChevronRight className="w-3 h-3 text-white" />
                                </div>
                                <span className="text-slate-700 text-sm">{item}</span>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template11/contact')}
                        className="w-full py-4 bg-slate-900 text-white text-sm font-medium tracking-wider uppercase flex items-center justify-center gap-3"
                    >
                        Book Consultation <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop: Bespoke - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-gradient-to-br from-slate-100 to-blue-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-4">
                                <div className="aspect-[4/5] overflow-hidden rounded-lg">
                                    <img
                                        src="https://placehold.co/400x500/F8FAFC/1E3A5F?text=Tailoring+1"
                                        alt="Tailoring"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="aspect-square overflow-hidden rounded-lg">
                                    <img
                                        src="https://placehold.co/300x300/F1F5F9/1E3A5F?text=Fabric"
                                        alt="Fabric Selection"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <div className="space-y-4 pt-8">
                                <div className="aspect-square overflow-hidden rounded-lg">
                                    <img
                                        src="https://placehold.co/300x300/E2E8F0/1E3A5F?text=Details"
                                        alt="Details"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="aspect-[4/5] overflow-hidden rounded-lg">
                                    <img
                                        src="https://placehold.co/400x500/CBD5E1/1E3A5F?text=Tailoring+2"
                                        alt="Tailoring"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <span className="text-blue-800 text-sm font-medium tracking-[0.2em] uppercase">Bespoke Service</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mt-3 mb-6">
                                Made to Measure Excellence
                            </h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-8">
                                Experience the art of bespoke tailoring. Our master tailors craft each garment
                                to your exact specifications, ensuring a perfect fit that reflects your unique style.
                            </p>
                            <div className="space-y-4 mb-8">
                                {[
                                    'Personal consultation with style experts',
                                    'Premium fabric selection from finest mills',
                                    'Multiple fittings for perfect fit',
                                    'Delivery in 3-4 weeks'
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-4">
                                        <div className="w-6 h-6 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                                            <ChevronRight className="w-4 h-4 text-white" />
                                        </div>
                                        <span className="text-slate-700">{item}</span>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={() => navigate('/template11/contact')}
                                className="group px-8 py-4 bg-slate-900 text-white text-sm font-medium tracking-wider uppercase hover:bg-slate-800 transition-all duration-300 flex items-center gap-3"
                            >
                                Book Consultation
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== TESTIMONIALS ===== */}
            {/* Mobile: swipeable carousel */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <span className="text-blue-800 text-xs font-medium tracking-[0.2em] uppercase">Testimonials</span>
                    <h2 className="text-xl font-light text-slate-900 mt-2 mb-5">What Gentlemen Say</h2>
                    <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-1">
                                    <div className="bg-slate-50 p-6 rounded-lg">
                                        <Quote className="w-8 h-8 text-blue-200 mb-3" />
                                        <p className="text-slate-700 text-[14px] leading-relaxed mb-5">
                                            "{testimonial.text}"
                                        </p>
                                        <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                                            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                                                <span className="text-slate-700 font-semibold text-sm">{testimonial.name[0]}</span>
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900 text-sm">{testimonial.name}</p>
                                                <p className="text-slate-500 text-xs">{testimonial.event} &bull; {testimonial.location}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-5">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className={`h-2 rounded-full transition-all duration-300 ${activeTestimonial === i ? 'w-6 bg-slate-900' : 'w-2 bg-slate-300'}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop: Testimonials grid - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-blue-800 text-sm font-medium tracking-[0.2em] uppercase">Testimonials</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mt-3">What Gentlemen Say</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-slate-50 p-8 rounded-lg">
                                <Quote className="w-10 h-10 text-blue-200 mb-4" />
                                <p className="text-slate-700 leading-relaxed mb-6">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                                    <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                                        <span className="text-slate-700 font-semibold">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">{testimonial.name}</p>
                                        <p className="text-slate-500 text-sm">{testimonial.event} &bull; {testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== NEWSLETTER ===== */}
            {/* Mobile: stacked rounded */}
            <section className="lg:hidden py-10 bg-slate-900 text-white">
                <div className="px-5 text-center">
                    <Briefcase className="w-8 h-8 text-slate-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-light text-white mb-3">Join the Gentlemen's Club</h2>
                    <p className="text-slate-400 text-[14px] mb-6">
                        Be the first to know about new collections, exclusive offers, and style tips.
                    </p>
                    <div className="flex flex-col gap-3 max-w-sm mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="px-5 py-3.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-slate-500 transition-colors text-sm"
                        />
                        <button className="w-full px-8 py-3.5 bg-white text-slate-900 text-sm font-semibold tracking-wider uppercase rounded-xl">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop: Newsletter - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-slate-900 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Briefcase className="w-10 h-10 text-slate-400 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
                        Join the Gentlemen's Club
                    </h2>
                    <p className="text-slate-400 text-lg mb-8">
                        Be the first to know about new collections, exclusive offers, and style tips.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-slate-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-white text-slate-900 text-sm font-semibold tracking-wider uppercase hover:bg-slate-100 transition-colors whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== INSTAGRAM ===== */}
            <section className="py-12 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 lg:mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-slate-700" />
                            <span className="text-slate-700 font-medium">@allure_menswear</span>
                        </div>
                        <h2 className="text-2xl lg:text-4xl font-light text-slate-900">#DressWithAllure</h2>
                    </div>

                    {/* Mobile: 3-col tight grid */}
                    <div className="grid grid-cols-3 gap-0.5 lg:hidden">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="aspect-square overflow-hidden">
                                <img
                                    src={`https://placehold.co/300x300/F8FAFC/1E3A5F?text=Style+${index + 1}`}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ))}
                    </div>
                    {/* Desktop: 6-col with hover */}
                    <div className="hidden lg:grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/F8FAFC/1E3A5F?text=Style+${index + 1}`}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SERVICES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-8 bg-slate-50 border-t border-slate-100">
                <div className="px-5">
                    <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory scrollbar-hide">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'On orders above \u20B95,000' },
                            { icon: RefreshCw, title: 'Easy Exchange', desc: '15-day exchange policy' },
                            { icon: Shield, title: 'Quality Assured', desc: 'Premium fabrics only' },
                            { icon: Clock, title: 'Expert Styling', desc: 'Free consultation' },
                        ].map((service, i) => (
                            <div key={i} className="flex-shrink-0 w-[55vw] snap-start flex items-center gap-3">
                                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                                    <service.icon className="w-4 h-4 text-slate-700" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-900 text-sm">{service.title}</p>
                                    <p className="text-slate-500 text-xs">{service.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: Services - EXACT ORIGINAL */}
            <section className="hidden lg:block py-12 bg-slate-50 border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-slate-700" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Free Shipping</p>
                                <p className="text-slate-500 text-sm">On orders above {'\u20B9'}5,000</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-slate-700" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Easy Exchange</p>
                                <p className="text-slate-500 text-sm">15-day exchange policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-slate-700" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Quality Assured</p>
                                <p className="text-slate-500 text-sm">Premium fabrics only</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Clock className="w-5 h-5 text-slate-700" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Expert Styling</p>
                                <p className="text-slate-500 text-sm">Free consultation</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template11;
